import { Link as RouterLink } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { getTransactions } from '../../services/transactionService'
import { Box, Typography, Button, IconButton } from '@mui/material'
import { RfhColors } from '../../config/florimixColors'
import { TransactionSkeleton } from './TransactionSkeleton'
import { TransactionListItem } from './TransactionListItem'
import {
  type TransactionTabValue,
  getTransactionTabLabel,
  isTransactionInTab,
} from '../../utils/transactionStatusUtils'
import { applyTransactionFilters } from '../../utils/transactionFilterUtils'
import CrateIcon from '@floriday/floriday-icons/CrateIcon'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { useTransactionFilters } from '../../context/transactionFilterContext'
import { useActiveCustomerNumber } from '../../hooks/useActiveCustomerNumber'
import { useOptionalTransactionSelection } from '../../context/transactionSelectionContext'
import { TransactionSelectAllBar } from './TransactionSelectAllBar'
import { TransactionSelectAllBarSkeleton } from './TransactionSelectAllBarSkeleton'
import { TransactionDownloadBar } from './TransactionDownloadBar'

const MAX_TRANSACTIONS_PER_TAB = 5
const FULL_PAGE_LOADING_SKELETON_COUNT = 8

type TransactionListProps = {
  selectedTab: TransactionTabValue
  listLimit?: number | null
  showViewAllButton?: boolean
}

export const TransactionList = ({
  selectedTab,
  listLimit = MAX_TRANSACTIONS_PER_TAB,
  showViewAllButton = true,
}: TransactionListProps) => {
  const { t } = useTranslation()
  const [expandedTransactionIdByTab, setExpandedTransactionIdByTab] = useState<
    Partial<Record<TransactionTabValue, string | null>>
  >({})
  const activeCustomerNumber = useActiveCustomerNumber()
  const selection = useOptionalTransactionSelection()
  const [isDownloadLoading, setIsDownloadLoading] = useState(false)

  const {
    data: transactions = [],
    isPending,
    isFetching,
  } = useQuery({
    queryKey: ['transactions', activeCustomerNumber],
    queryFn: async () => getTransactions(activeCustomerNumber as number),
    enabled: !!activeCustomerNumber,
  })

  const {
    searchQuery,
    selectedTransactionTypes,
    dateFilter,
    selectedBusinessAssociationIds,
  } = useTransactionFilters()

  const filteredTransactions = applyTransactionFilters(
    transactions.filter(transaction =>
      isTransactionInTab(transaction.status, selectedTab)
    ),
    {
      searchQuery,
      selectedTransactionTypes,
      dateFilter,
      selectedBusinessAssociationIds,
    }
  )

  const visibleTransactions =
    listLimit === null
      ? filteredTransactions
      : filteredTransactions.slice(0, listLimit)

  const filteredTransactionIds = filteredTransactions.flatMap(transaction =>
    transaction.id ? [transaction.id] : []
  )

  const visibleSelectedCount = selection
    ? selection.selectedTransactionIds.filter(id =>
        filteredTransactionIds.includes(id)
      ).length
    : 0

  const handleDownload = async () => {
    if (!selection || visibleSelectedCount === 0) return

    const selectedTransactions = filteredTransactions.filter(
      transaction =>
        transaction.id &&
        selection.selectedTransactionIds.includes(transaction.id)
    )

    setIsDownloadLoading(true)
    try {
      const { generateTransactionsPdf } =
        await import('../../utils/generateTransactionsPdf')
      await generateTransactionsPdf(selectedTransactions)
      selection.deselectAll()
    } catch (error) {
      console.error('Error generating transactions PDF:', error)
    } finally {
      setIsDownloadLoading(false)
    }
  }

  const loadingSkeletonCount =
    listLimit === null ? FULL_PAGE_LOADING_SKELETON_COUNT : listLimit

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        paddingTop: selection ? 0 : 2,
        background: RfhColors.white,
      }}
    >
      {selection &&
        (isPending || isFetching ? (
          <TransactionSelectAllBarSkeleton />
        ) : (
          <TransactionSelectAllBar transactionIds={filteredTransactionIds} />
        ))}
      {isPending || isFetching ? (
        Array.from({ length: loadingSkeletonCount }).map((_, index) => (
          <TransactionSkeleton
            key={`transaction-skeleton-${index}`}
            striped={index % 2 === 1}
          />
        ))
      ) : visibleTransactions.length === 0 ? (
        <Box
          sx={{
            padding: theme => theme.spacing(1, 2, 2),
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <IconButton sx={{ backgroundColor: '#F2F2F2', padding: 2 }}>
            <CrateIcon sx={{ fontSize: 30, color: '#98979C' }} />
          </IconButton>
          <Typography variant='h3' color={RfhColors.darkGrey}>
            {t('transactions.empty.title')}
          </Typography>
          <Typography variant='body1' color={RfhColors.darkGrey}>
            {t('transactions.empty.description', {
              selectedTab: getTransactionTabLabel(selectedTab, t),
            })}
          </Typography>
        </Box>
      ) : (
        visibleTransactions.map((transaction, index) => (
          <TransactionListItem
            key={transaction.id}
            transaction={transaction}
            index={index}
            isExpanded={
              expandedTransactionIdByTab[selectedTab] === undefined
                ? index === 0
                : expandedTransactionIdByTab[selectedTab] === transaction.id
            }
            onExpandedChange={isExpanded =>
              setExpandedTransactionIdByTab(current => ({
                ...current,
                [selectedTab]: isExpanded ? transaction.id : null,
              }))
            }
          />
        ))
      )}
      {showViewAllButton &&
        !isPending &&
        !isFetching &&
        visibleTransactions.length !== 0 && (
          <Button
            component={RouterLink}
            to='/transactions'
            variant='text'
            sx={{
              marginTop: theme => theme.spacing(1),
              color: RfhColors.royalPurple,
              textDecoration: 'underline',
            }}
          >
            {t('transactions.viewAll')}
          </Button>
        )}
      {selection && (
        <TransactionDownloadBar
          onDownload={handleDownload}
          isLoading={isDownloadLoading}
          count={visibleSelectedCount}
        />
      )}
    </Box>
  )
}
